import axios from "axios";
import {
    ICheckoutOrder,
    IConfirmStripeOrderArgs,
    ICreateStripeOrderArgs,
    ICreateStripeOrderRes,
    IPaypalOrderArgs,
    PaypalCreateResponseTO,
} from "@/models/queries/checkout";
import { getUrl } from "@/utils/queries/utils/utils";
import { FETCH_TIMEOUT } from "@/utils/constants";
import { getHeadersAuth } from ".";

export const createStripeOrder = async (
    orderData: ICreateStripeOrderArgs,
    authenticated: boolean
): Promise<ICreateStripeOrderRes> => {
    const authHeaders = authenticated ? await getHeadersAuth() : {};
    const url = getUrl(
        `${process.env.NEXT_PUBLIC_BASE_URL}`,
        "/order/create/stripe"
    );

    try {
        const { data } = await axios.post(url, orderData, {
            ...authHeaders,
            timeout: FETCH_TIMEOUT,
        });
        return data;
    } catch (err) {
        if (axios.isAxiosError(err)) {
            // Log dettagliato per debugging
            const errorMessage =
                err.response?.data?.message || err.message || "Network Error";

            const errorDetails = {
                message: errorMessage,
                status: err.response?.status,
                statusText: err.response?.statusText,
                url,
                code: err.code,
            };

            // eslint-disable-next-line no-console
            console.error("Stripe order creation error:", errorDetails);

            // Se è un errore di rete (code: 'ERR_NETWORK' o 'ECONNABORTED'), fornisci un messaggio più utile
            if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED") {
                throw new Error(
                    `Network error: Unable to connect to payment service. Please check your internet connection and try again.`
                );
            }

            throw new Error(errorMessage);
        } else {
            // Just a stock error
            throw err;
        }
    }
};

export interface IConfirmStripeOrderRes {
    paymentIntentId: string;
}

export const confirmStripeOrder = async (
    orderData: IConfirmStripeOrderArgs,
    authenticated: boolean
): Promise<IConfirmStripeOrderRes> => {
    const authHeaders = authenticated ? await getHeadersAuth() : {};
    const url = getUrl(
        `${process.env.NEXT_PUBLIC_BASE_URL}`,
        "/order/confirm/stripe"
    );

    try {
        const { data } = await axios.post(url, orderData, {
            ...authHeaders,
            timeout: FETCH_TIMEOUT,
        });
        return data;
    } catch (err) {
        if (axios.isAxiosError(err)) {
            const errorMessage =
                err.response?.data?.message || err.message || "Network Error";

            // eslint-disable-next-line no-console
            console.error("Stripe order confirmation error:", {
                message: errorMessage,
                status: err.response?.status,
                url,
                code: err.code,
            });

            if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED") {
                throw new Error(
                    `Network error: Unable to confirm payment. Please check your internet connection and try again.`
                );
            }

            throw new Error(errorMessage);
        } else {
            throw err;
        }
    }
};

export const createPaypal = async (
    body: ICheckoutOrder,
    authenticated: boolean
): Promise<PaypalCreateResponseTO> => {
    const headers = authenticated ? await getHeadersAuth() : {};
    try {
        const { data } = await axios.post(
            getUrl(
                `${process.env.NEXT_PUBLIC_BASE_URL}`,
                "/order/create/paypal"
            ),
            body,
            headers
        );
        return data;
    } catch (err) {
        if (axios.isAxiosError(err)) {
            // Access to config, request, and response
            throw new Error(err.response?.data.message ?? err.message);
        } else {
            // Just a stock error
            throw err;
        }
    }
};

export const confirmPaypal = async (
    body: IPaypalOrderArgs,
    authenticated: boolean
) => {
    try {
        const headers = authenticated ? await getHeadersAuth() : {};
        const { data } = await axios.post(
            getUrl(
                `${process.env.NEXT_PUBLIC_BASE_URL}`,
                "/order/confirm/paypal"
            ),
            body,
            headers
        );
        return data;
    } catch (err) {
        if (axios.isAxiosError(err)) {
            // Access to config, request, and response
            throw new Error(err.response?.data.message ?? err.message);
        } else {
            // Just a stock error
            throw err;
        }
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const requestAQuoteAPI = async (body: any) => {
    try {
        // eslint-disable-next-line no-param-reassign
        body.alreadyBookAFlight = body.alreadyBookAFlight === "YES";
        // eslint-disable-next-line no-param-reassign
        body.needTravelInsurance = body.needTravelInsurance === "YES";
        // eslint-disable-next-line no-param-reassign
        body.needAssistance = body.needAssistance === "YES";
        // eslint-disable-next-line no-param-reassign
        body.marketingConsent = body.marketingConsent === "YES";

        const { data } = await axios.post(
            getUrl(`${process.env.NEXT_PUBLIC_BASE_URL}`, "/request-a-quote"),
            body
        );
        return data;
    } catch (err) {
        if (axios.isAxiosError(err)) {
            // Access to config, request, and response
            throw new Error(err.response?.data.message ?? err.message);
        } else {
            // Just a stock error
            throw err;
        }
    }
};

export const hasAcceptedPrivacyPolicy = () => {
    return localStorage.getItem("privacyAccepted") === "true";
};
